import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Save, Loader2, Plus, Settings, Play, Eye, ZoomIn, ZoomOut, Maximize, LayoutGrid } from 'lucide-react';
import dagre from 'dagre';
import { setStepNextStepId, replaceStepTarget, clearStepNextStepId, shouldPersistManualEdge, isNonConditionNode, getNextStepIds, isForwarderId, isValidEntryTarget, resolveEntryStepId, normalizeWorkflowGraph, purgeNodeReferences, computeReachableIds } from '../lib/workflowEdgeUtils';
import { ZOOM_STEP, wheelZoomFactor, zoomAtPoint } from '../lib/canvasZoom';
import { getAllFieldsFromSteps } from '../lib/formUtils';
import { errorText } from '../lib/apiErrors';
import ColorField from '../components/ColorField';
import EventRegistrationSettings from '../components/forms/EventRegistrationSettings';
import { hasEmailField as formHasEmailField } from '../lib/formEventRegistration';
import { deriveNumeralColor, contrastRatio } from '../lib/stepperColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Node type configurations matching the mockup
const NODE_TYPES = {
    start:     { label: 'Start',       color: '#1A7F4E', bg: '#E3F3EA', desc: 'Entry point' },
    form:      { label: 'Form Page',   color: '#6366F1', bg: '#EFF6FF', desc: 'Collect user input' },
    custom:    { label: 'Custom Page', color: '#7C3AED', bg: '#F5F3FF', desc: 'Static content page' },
    condition: { label: 'Condition',   color: '#B45309', bg: '#FCEFDD', desc: 'Branch on logic' },
    forwarder: { label: 'Forwarder',   color: '#1A7F4E', bg: '#F0FDFA', desc: 'Send data externally' },
    redirect:  { label: 'Redirect',    color: '#6366F1', bg: '#EFF6FF', desc: 'Send to URL' },
    end:       { label: 'End',         color: '#C2363F', bg: '#FBE7E8', desc: 'Workflow exit' },
};

const PALETTE_ITEMS = [
    { type: 'form',      name: 'Form Page',   desc: 'Collect user input',   icon: 'list' },
    { type: 'custom',    name: 'Custom Page', desc: 'Static content page',  icon: 'file' },
    { type: 'condition', name: 'Condition',   desc: 'Branch on logic',      icon: 'branch' },
    { type: 'forwarder', name: 'Forwarder',   desc: 'Send data externally', icon: 'send' },
    { type: 'redirect',  name: 'Redirect',    desc: 'Send to URL',          icon: 'redirect' },
    { type: 'end',        name: 'End',         desc: 'Workflow exit point',  icon: 'stop' },
];

// Icon SVG paths (lucide-style)
const ICONS = {
    play: 'M6 3 20 12 6 21 6 3',
    list: 'M17 6.1H3 M21 12.1H3 M15.1 18H3',
    file: 'M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z M16 3v5h5',
    branch: 'M12 2 2 22h20L12 2z',
    send: 'M4 12h16 M4 6h16 M4 18h12',
    redirect: 'm21 16-9 5-9-5 M21 8l-9 5-9-5 9-5 9 5z M3 8v8',
    stop: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20 M15 9l-6 6 M9 9l6 6',
};

const NODE_W = 180;
const NODE_H = 80;
const CANVAS_WIDTH = 1600; // widened from 800px to accommodate branching layouts

// ─── Dagre auto-layout (Issue 11) ────────────────────────────────────────────
// Uses dagre as a standalone library (same pattern as CampaignBuilder.jsx).
// WorkflowCanvas uses a custom SVG canvas, NOT ReactFlow, so dagre must be
// integrated manually.
function computeDagreLayout(nodes, edges) {
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 50, marginx: 40, marginy: 40 });
    g.setDefaultEdgeLabel(() => ({}));

    nodes.forEach(n => {
        g.setNode(n.id, { width: NODE_W, height: NODE_H });
    });
    edges.forEach(e => {
        if (g.hasNode(e.from) && g.hasNode(e.to)) {
            g.setEdge(e.from, e.to);
        }
    });

    dagre.layout(g);

    // Map dagre positions back to canvas coordinates (top-left origin)
    const positioned = nodes.map(n => {
        const pos = g.node(n.id);
        if (!pos) return n;
        return { ...n, x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2 };
    });

    // Center the layout horizontally within CANVAS_WIDTH.
    // Dagre starts from marginx (left edge), so narrow layouts end up
    // hugging the left side. Compute the bounding box and shift right.
    if (positioned.length > 0) {
        const minX = Math.min(...positioned.map(n => n.x));
        const maxX = Math.max(...positioned.map(n => n.x + NODE_W));
        const layoutWidth = maxX - minX;
        const offsetX = (CANVAS_WIDTH - layoutWidth) / 2 - minX;
        return positioned.map(n => ({ ...n, x: n.x + offsetX }));
    }

    return positioned;
}

function NodeIcon({ type, size = 16 }) {
    const cfg = NODE_TYPES[type];
    const iconKey = PALETTE_ITEMS.find(p => p.type === type)?.icon || 'file';
    const path = ICONS[iconKey] || ICONS.file;
    return (
        <div style={{ width: 28, height: 28, borderRadius: 6, background: cfg?.bg, color: cfg?.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d={path} />
            </svg>
        </div>
    );
}

function PaletteIcon({ type, size = 16 }) {
    return <NodeIcon type={type} size={size} />;
}

/**
 * A saved form document, with the read-side `event_registration_event` join kept. GET, POST and PUT all answer with the join
 * (`_serialize_with_registration_event`), so this only matters for a response that lacks the key (an older runtime, or a
 * test double): replacing the form with it would turn the "Registers on" chip off. The join still holds while the
 * setting names the same event.
 */
function keepRegistrationEvent(saved, previous) {
    if (!saved || saved.event_registration_event !== undefined || previous?.event_registration_event === undefined) return saved;
    const same = saved.settings?.event_registration?.event_id === previous.settings?.event_registration?.event_id;
    return same ? { ...saved, event_registration_event: previous.event_registration_event } : saved;
}

/** The event a form document's `settings.event_registration` names, or null. */
const registrationIdOf = (doc) => doc?.settings?.event_registration?.event_id || null;

export default function WorkflowCanvas() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);
    const [forwarders, setForwarders] = useState([]);
    // Global form settings panel toggle (Settings button in action bar)
    const [showSettings, setShowSettings] = useState(false);
    // The event the SAVED form registers on (#919): taken from every document the server hands back, so the Event
    // registration section knows a pick or switch-off is unsaved ("Save to apply"). The section unmounts whenever the
    // panel switches to Node Properties, so it can't keep that itself.
    const [savedRegistrationId, setSavedRegistrationId] = useState(null);
    // Leave guard — Back / Manage Forwarders / tab close prompt while dirty.
    const { isDirty, setIsDirty, blockedNavigate, showNavConfirm, confirmLeave, cancelLeave } = useUnsavedChanges(navigate);
    const markDirty = () => setIsDirty(true);

    // Reachability from the entry step (for the "Unreachable" node badge and
    // the Save warning). Uses the same walk as the public renderer's stepper.
    const reachableIds = useMemo(
        () => computeReachableIds(form?.steps || [], forwarders, resolveEntryStepId(form)),
        [form, forwarders]
    );

    // Flat list of all input fields across all question steps (for redirect
    // query parameter field dropdown). Deduped by field_id, skips router steps.
    const allFormFields = useMemo(() => getAllFieldsFromSteps(form?.steps || []), [form?.steps]);

    // "Registers on: {event}" header chip (#919, E55). It reads the form document (`event_registration_event`), so the
    // canvas still makes ONE request. A form with no target has none; a target the server could not resolve (a deleted
    // event, or a null value beside a saved setting that names one) reads "event unavailable". A document with no such
    // key at all says nothing: the join is only added by GET /forms/{id}, so its absence is not "unavailable". It shows
    // what is SAVED: an unsaved pick in Form Settings (where the live setting can name an event the join has not read
    // yet) changes it only once Save stores it.
    const registration = useMemo(() => {
        const target = form?.event_registration_event;
        if (target === undefined) return null;
        if (target && typeof target === 'object' && target.name && target.status !== 'deleted') {
            return { name: String(target.name), unavailable: false };
        }
        return target || savedRegistrationId ? { name: '', unavailable: true } : null;
    }, [form?.event_registration_event, savedRegistrationId]);

    // Event registration is a form-wide setting, so it lives in Form Settings (#919 QA round 2; the page editor no longer
    // has it). A pick or switch-off is a canvas edit like any other: it marks the canvas unsaved and rides the next Save.
    const registrationDirty = registrationIdOf(form) !== savedRegistrationId;
    const updateEventRegistration = (value) => {
        setForm(prev => ({ ...prev, settings: { ...(prev?.settings || {}), event_registration: value } }));
        setIsDirty(true);
    };
    // Whether any step can supply the respondent's email (the section warns when none can).
    const registrationHasEmail = useMemo(() => formHasEmailField({ steps: form?.steps || [], form }), [form]);

    // Drag state — { kind: 'node', id, offsetX, offsetY } or null
    const [drag, setDrag] = useState(null);
    // Connection drag — { fromNode, fromSide, mouseX, mouseY } or null
    const [connecting, setConnecting] = useState(null);
    // Canvas pan — { x, y } offset applied to canvas-inner
    const [pan, setPan] = useState({ x: 0, y: 0 });
    // Canvas zoom — scale factor (1 = 100%)
    const [zoom, setZoom] = useState(1);
    // Panning state — { startX, startY, panX, panY } or null
    const [panning, setPanning] = useState(null);

    // Ref to the canvas inner div for coordinate calculations
    const canvasInnerRef = useRef(null);

    // The canvas area div renders conditionally (behind the loading spinner),
    // so the wheel listener is attached via a callback ref (attachCanvasWheel
    // below) — a mount-time effect would run before the div exists and its
    // deps never change when the canvas appears.
    const wheelElRef = useRef(null);
    const isInteractingRef = useRef(false);

    // View refs mirror zoom/pan so the wheel listener (attached once) always
    // reads the committed view without re-attaching on every render.
    const zoomRef = useRef(zoom);
    const panRef = useRef(pan);
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);
    useEffect(() => { panRef.current = pan; }, [pan]);

    // Shared zoom setter for the wheel and the +/- buttons: one clamp, one
    // place. With an anchor (client coords) the pan is recomputed so the canvas
    // point under the anchor stays under it — cursor-anchored zoom.
    const applyZoom = useCallback((nextZoom, anchor) => {
        const rect = anchor ? canvasInnerRef.current?.getBoundingClientRect() : null;
        const { zoom: next, pan: nextPan } = zoomAtPoint({
            currentZoom: zoomRef.current,
            currentPan: panRef.current,
            nextZoom,
            clientX: anchor ? anchor.clientX : undefined,
            clientY: anchor ? anchor.clientY : undefined,
            rect,
        });
        if (next === zoomRef.current
            && nextPan.x === panRef.current.x
            && nextPan.y === panRef.current.y) return;
        zoomRef.current = next;
        panRef.current = nextPan;
        setZoom(next);
        setPan(nextPan);
    }, []);

    const handleZoomIn = () => applyZoom(zoomRef.current + ZOOM_STEP);
    const handleZoomOut = () => applyZoom(zoomRef.current - ZOOM_STEP);
    const resetView = () => {
        zoomRef.current = 1;
        panRef.current = { x: 0, y: 0 };
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    // Wheel zoom over the canvas: a non-passive listener so preventDefault()
    // keeps the shell's scroll container still (and no passive-listener console
    // warning). Ignored during an active drag / pan / connect. Both pinch
    // (ctrlKey) and two-finger trackpad scroll zoom — decision recorded in
    // docs/runtime/core/marketing/forms/README.md.
    // isInteractingRef is synced in a layout effect so the guard is already
    // fresh before any wheel event can fire after a drag/pan/connect starts.
    useLayoutEffect(() => {
        isInteractingRef.current = Boolean(drag || connecting || panning);
    }, [drag, connecting, panning]);

    const handleWheel = useCallback((e) => {
        if (isInteractingRef.current) return;
        e.preventDefault();
        applyZoom(
            zoomRef.current * wheelZoomFactor(e.deltaY, e.deltaMode),
            { clientX: e.clientX, clientY: e.clientY },
        );
    }, [applyZoom]);

    // Callback ref: (re)attaches the wheel listener whenever the canvas area
    // element mounts and detaches when it unmounts. Listener is non-passive so
    // preventDefault() keeps the shell's scroll container still.
    const attachCanvasWheel = useCallback((el) => {
        if (wheelElRef.current && wheelElRef.current !== el) {
            wheelElRef.current.removeEventListener('wheel', handleWheel);
        }
        wheelElRef.current = el;
        if (el) el.addEventListener('wheel', handleWheel, { passive: false });
    }, [handleWheel]);

    const isNew = !formId || formId === 'new';

    // ── Load form ────────────────────────────────────────────────────────
    useEffect(() => {
        if (isNew) {
            setForm({ name: 'Untitled Workflow', description: '', steps: [], forwarders: [], settings: {}, styling: {} });
            setNodes([
                { id: 'node-start', type: 'start', title: 'Workflow Start', meta: 'Entry point', x: 310, y: 20 },
            ]);
            setEdges([]);
            setLoading(false);
            return;
        }
        axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() })
            .then(res => {
                const f = res.data;
                // Enrich forwarders with step_id if missing (legacy data).
                // This must happen BEFORE setForwarders and buildNodesFromForm
                // so that step_id is stable across reloads and findNodeIdByStepId
                // can resolve edges. The enriched step_id is also persisted on
                // the next save, so subsequent loads are stable.
                if (f.forwarders && f.forwarders.length > 0) {
                    // Fix up stale fwd_* references in steps (legacy data without step_id)
                    f.forwarders = f.forwarders.map((fwd, idx) => {
                        if (fwd.step_id) return fwd;
                        const newStepId = `fwd_${Date.now()}_${idx}`;
                        // If any step references this forwarder by an old generated ID,
                        // we need to fix up the reference. We can't know the old ID,
                        // but we can find steps whose next_step_id starts with "fwd_"
                        // and doesn't match any forwarder's step_id — those are
                        // stale references from previous loads.
                        return { ...fwd, step_id: newStepId };
                    });
                    // Fix up stale fwd_* references in steps
                    if (f.steps) {
                        f.steps = f.steps.map(s => {
                            const updated = { ...s };
                            // MULTI-EDGE: next_step_id can be string (legacy) or array.
                            // Normalize to array for fixup, then write back as array.
                            const nextIds = getNextStepIds(updated);
                            if (nextIds.length > 0) {
                                const fixedIds = nextIds.map(id => {
                                    if (id && id.startsWith('fwd_')) {
                                        const matchIdx = f.forwarders.findIndex(fwd => fwd.step_id === id);
                                        if (matchIdx === -1) {
                                            // Stale reference — parse the index suffix and map to new step_id
                                            const parts = id.split('_');
                                            const oldIdx = parseInt(parts[parts.length - 1], 10);
                                            if (!isNaN(oldIdx) && f.forwarders[oldIdx]) {
                                                return f.forwarders[oldIdx].step_id;
                                            }
                                        }
                                    }
                                    return id;
                                });
                                updated.next_step_id = fixedIds;
                            }
                            // Fix up branch targets too
                            if (updated.branch) {
                                if (updated.branch.default_target && updated.branch.default_target.startsWith('fwd_')) {
                                    const matchIdx = f.forwarders.findIndex(fwd => fwd.step_id === updated.branch.default_target);
                                    if (matchIdx === -1) {
                                        const parts = updated.branch.default_target.split('_');
                                        const oldIdx = parseInt(parts[parts.length - 1], 10);
                                        if (!isNaN(oldIdx) && f.forwarders[oldIdx]) {
                                            updated.branch = { ...updated.branch, default_target: f.forwarders[oldIdx].step_id };
                                        }
                                    }
                                }
                                if (updated.branch.cases) {
                                    updated.branch = {
                                        ...updated.branch,
                                        cases: updated.branch.cases.map(c => {
                                            if (c.targetStepId && c.targetStepId.startsWith('fwd_')) {
                                                const matchIdx = f.forwarders.findIndex(fwd => fwd.step_id === c.targetStepId);
                                                if (matchIdx === -1) {
                                                    const parts = c.targetStepId.split('_');
                                                    const oldIdx = parseInt(parts[parts.length - 1], 10);
                                                    if (!isNaN(oldIdx) && f.forwarders[oldIdx]) {
                                                        return { ...c, targetStepId: f.forwarders[oldIdx].step_id };
                                                    }
                                                }
                                            }
                                            return c;
                                        })
                                    };
                                }
                            }
                            return updated;
                        });
                    }
                }
                // Heal the graph on load (dangling ids, one step target per
                // node, legacy entry pointer) so the canvas shows exactly what
                // the next save will persist.
                const healed = normalizeWorkflowGraph(f);
                const fixed = { ...f, steps: healed.steps, forwarders: healed.forwarders, entry_step_id: healed.entry_step_id };
                setForm(fixed);
                setSavedRegistrationId(registrationIdOf(fixed));
                setForwarders(fixed.forwarders || []);
                buildNodesFromForm(fixed);
            })
            .catch(() => { toast.error('Form not found'); navigate('/forms'); })
            .finally(() => setLoading(false));
        // Only re-fetch when the form changes. getAuthHeader is recreated by
        // AuthContext on every token refresh; having it here re-ran this
        // effect and silently reverted unsaved canvas edits to the saved copy.
        // getAuthHeader falls back to localStorage, so the closure is safe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId, isNew]);

    // ── Unified node ID lookup ─────────────────────────────────────────────
    // In the unified model, both steps and forwarders have step_id.
    // This helper finds the canvas node ID for a given step_id across both.
    const findNodeIdByStepId = (stepId, steps, forwarders) => {
        // Check steps first
        const stepIdx = steps.findIndex(s => s.step_id === stepId);
        if (stepIdx !== -1) return `node-step-${steps[stepIdx].step_id || stepIdx}`;
        // Check forwarders
        const fwd = forwarders.find(f => f.step_id === stepId);
        if (fwd) return `node-${fwd.step_id}`;
        return null;
    };

    // ── Update form styling helper (for Settings panel) ───────────────────
    const updateStyling = (key, value) => {
        setForm(prev => ({ ...prev, styling: { ...prev.styling, [key]: value } }));
        setIsDirty(true);
    };


    // ── Update a single step by ID (shared by redirect + font style IIFEs) ──
    const updateStep = (stepId, patch) => {
        setForm(prev => ({
            ...prev,
            steps: (prev?.steps || []).map(s =>
                s.step_id === stepId ? { ...s, ...patch } : s
            )
        }));
        setIsDirty(true);
    };


    // ── Build visual nodes + derived edges from form data ──────────────────
    // Edges are now derived from branch config (Issue 7/8/9), replacing the
    // naive linear chain where explicit routing exists.
    const buildNodesFromForm = (f) => {
        const nodeList = [];
        const edgeList = [];
        let yPos = 170;

        // Start node — use stored position from canvas_meta if available
        const startStoredPos = (f.canvas_meta || {})['node-start'] || {};
        nodeList.push({ id: 'node-start', type: 'start', title: 'Workflow Start', meta: 'Entry point', x: startStoredPos.x ?? 310, y: startStoredPos.y ?? 20 });

        const steps = f.steps || [];
        const stepNodeId = (step, idx) => `node-step-${step.step_id || step.id || idx}`;

        // Track which steps have explicit outgoing edges (from condition cases/branches)
        const explicitlySourced = new Set();

        // First pass: build nodes
        steps.forEach((step, idx) => {
            const nodeId = stepNodeId(step, idx);
            const isCustom = step.kind === 'custom_page' || step.kind === 'splash';
            const isEnd = step.kind === 'end';
            const isRedirect = step.kind === 'redirect';
            const fieldCount = step.fields?.length || 0;

            // Determine condition meta
            let meta = isCustom ? 'Custom page' : isEnd ? 'Workflow exit' : isRedirect ? 'URL redirect' : `${fieldCount} field${fieldCount !== 1 ? 's' : ''}`;
            if (step.branch) {
                const branchCount = step.branch.mode === 'switch_case'
                    ? (step.branch.cases?.length || 0) + 1 // +1 for default
                    : step.branch.branches?.length || 0;
                meta = `${branchCount} branch${branchCount !== 1 ? 'es' : ''} · ${step.branch.mode === 'switch_case' ? 'Switch Case' : 'If/Else'}`;
            }

            const nodeType = step.branch ? 'condition' : (isEnd ? 'end' : (isRedirect ? 'redirect' : (isCustom ? 'custom' : 'form')));

            // Use stored canvas position if available (Issue 10)
            const storedPos = step.canvas || {};
            nodeList.push({
                id: nodeId,
                type: nodeType,
                title: step.title || `Step ${idx + 1}`,
                meta,
                x: storedPos.x ?? 310,
                y: storedPos.y ?? yPos,
                stepId: step.step_id || step.id, stepIndex: idx,
            });
            yPos += 150;
        });

        // canvas_meta stores positions for non-step nodes (start, end, forwarder, redirect)
        const meta = f.canvas_meta || {};

        // Forwarder nodes — unified model: forwarders use step_id like all other nodes.
        // If a forwarder doesn't have a step_id (legacy data), generate one on load.
        (f.forwarders || []).forEach((fwd, idx) => {
            const fwdStepId = fwd.step_id || `fwd_${Date.now()}_${idx}`;
            const nodeId = `node-${fwdStepId}`;
            const isActive = fwd.enabled !== false;
            const storedPos = meta[nodeId] || {};
            nodeList.push({
                id: nodeId, type: 'forwarder',
                title: fwd.name || `Forwarder ${idx + 1}`,
                meta: `${fwd.type === 'gohighlevel' ? 'GHL' : 'Custom API'} · ${isActive ? 'Active' : 'Inactive'}`,
                x: storedPos.x ?? 310, y: storedPos.y ?? yPos,
                stepId: fwdStepId,
            });
            yPos += 150;
        });

        // End node: if there are explicit end steps (kind: 'end'), they were
        // already added as nodes in the first pass above. Only add a default
        // hardcoded end node if the form was previously saved with one (tracked
        // via canvas_meta['node-end']). This prevents re-creating a deleted
        // default end node on every reload.
        const hasEndStep = steps.some(s => s.kind === 'end');
        const hadDefaultEnd = !!(f.canvas_meta || {})['node-end'];
        if (!hasEndStep && hadDefaultEnd) {
            const endStoredPos = meta['node-end'] || {};
            nodeList.push({ id: 'node-end', type: 'end', title: 'Workflow End', meta: 'Exit point', x: endStoredPos.x ?? 310, y: endStoredPos.y ?? yPos });
        }

        // Second pass: derive edges
        // 1. Start → entry step. Persisted as form.entry_step_id (a legacy
        // document without the key was healed to steps[0] on load). No entry
        // → no Start edge; the author sees the "Unreachable" badges.
        const entryId = resolveEntryStepId(f);
        const entryNodeId = entryId ? findNodeIdByStepId(entryId, steps, f.forwarders || []) : null;
        if (entryNodeId) {
            edgeList.push({ id: `edge-start-${entryNodeId}`, from: 'node-start', to: entryNodeId, fromSide: 'bottom', toSide: 'top', entry: true });
        } else if (steps.length === 0 && (f.forwarders || []).length === 0 && hadDefaultEnd) {
            edgeList.push({ id: 'edge-start-end', from: 'node-start', to: 'node-end', fromSide: 'bottom', toSide: 'top' });
        }

        // 2. Condition step edges (Issue 7/8/9)
        // For each condition step: incoming edge from source_node, outgoing labeled edges
        steps.forEach((step, idx) => {
            if (!step.branch) return;
            const condNodeId = stepNodeId(step, idx);

            // REMOVED: Incoming edge from source_node.
            // source_node is a DATA DEPENDENCY, not a flow edge.
            // The condition's incoming edge is derived from the preceding
            // step's next_step_id in Pass 3 below. Do NOT create an edge from
            // source_node. Do NOT add source_node to explicitlySourced.

            // Outgoing labeled edges (Issue 8/9)
            // CRITICAL: read targetStepId (camelCase) and default_target (snake_case)
            // — these are the exact key names ConditionEditor writes.
            if (step.branch.mode === 'switch_case') {
                (step.branch.cases || []).forEach((c, cIdx) => {
                    if (c.targetStepId) {
                        const targetNodeId = findNodeIdByStepId(c.targetStepId, steps, f.forwarders || []);
                        if (targetNodeId) {
                            explicitlySourced.add(condNodeId);
                            edgeList.push({
                                id: `edge-${condNodeId}-${targetNodeId}-${cIdx}`,
                                from: condNodeId, to: targetNodeId,
                                fromSide: 'bottom', toSide: 'top',
                                label: c.value || `Case ${cIdx + 1}`,
                                branchRef: { stepId: step.step_id, kind: 'case', idx: cIdx },
                            });
                        }
                    }
                });
                // Default edge
                if (step.branch.default_target) {
                    const targetNodeId = findNodeIdByStepId(step.branch.default_target, steps, f.forwarders || []);
                    if (targetNodeId) {
                        explicitlySourced.add(condNodeId);
                        edgeList.push({
                            id: `edge-${condNodeId}-${targetNodeId}-default`,
                            from: condNodeId, to: targetNodeId,
                            fromSide: 'bottom', toSide: 'top',
                            label: 'Default',
                            branchRef: { stepId: step.step_id, kind: 'default' },
                        });
                    }
                }
            } else if (step.branch.mode === 'if_else') {
                (step.branch.branches || []).forEach((b, bIdx) => {
                    if (b.targetStepId) {
                        const targetNodeId = findNodeIdByStepId(b.targetStepId, steps, f.forwarders || []);
                        if (targetNodeId) {
                            explicitlySourced.add(condNodeId);
                            edgeList.push({
                                id: `edge-${condNodeId}-${targetNodeId}-${bIdx}`,
                                from: condNodeId, to: targetNodeId,
                                fromSide: 'bottom', toSide: 'top',
                                label: b.name || `Branch ${bIdx + 1}`,
                                branchRef: { stepId: step.step_id, kind: 'branch', idx: bIdx },
                            });
                        }
                    }
                });
                // Default edge
                if (step.branch.default_target) {
                    const targetNodeId = findNodeIdByStepId(step.branch.default_target, steps, f.forwarders || []);
                    if (targetNodeId) {
                        explicitlySourced.add(condNodeId);
                        edgeList.push({
                            id: `edge-${condNodeId}-${targetNodeId}-default`,
                            from: condNodeId, to: targetNodeId,
                            fromSide: 'bottom', toSide: 'top',
                            label: 'Else (Default)',
                            branchRef: { stepId: step.step_id, kind: 'default' },
                        });
                    }
                }
            }
        });

        // 3. Non-condition step outgoing edges — explicit edges only.
        // Each non-condition step gets an outgoing edge ONLY if it has a
        // next_step_id (set when the user manually draws an edge on the canvas).
        // There is NO automatic linear-next fallback — if a step has no
        // next_step_id and no branch, it has no outgoing edge and the workflow
        // stops there (FLOW_END). This makes the visual workflow the source of
        // truth: if a node is not connected, it is not part of the flow.
        //
        // UNIFIED MODEL: next_step_id can point to another step OR a forwarder.
        // Forwarders are in the nodeList with their own stepId, so we look up
        // the target node by step_id across both steps and forwarders.
        steps.forEach((fromStep, i) => {
            const fromId = stepNodeId(fromStep, i);

            // Condition nodes derive edges from branch config — skip
            if (fromStep.branch) return;

            // Already has an explicit outgoing edge (from condition cases/branches)
            if (explicitlySourced.has(fromId)) return;

            // next_step_id override — manual edge(s) drawn by the user.
            // MULTI-EDGE: next_step_id can be an array of targets.
            // Target can be a step OR a forwarder (unified model).
            const nextIds = getNextStepIds(fromStep);
            nextIds.forEach((targetId, nIdx) => {
                const targetNodeId = findNodeIdByStepId(targetId, steps, f.forwarders || []);
                if (targetNodeId) {
                    explicitlySourced.add(fromId);
                    edgeList.push({ id: `edge-${fromId}-${targetNodeId}-${nIdx}`, from: fromId, to: targetNodeId, fromSide: 'bottom', toSide: 'top', manual: true });
                }
            });
        });

        // 4. Forwarder outgoing edges — unified model.
        // Forwarders use next_step_id just like steps. No hardcoded chain.
        // MULTI-EDGE: next_step_id can be an array of targets.
        (f.forwarders || []).forEach((fwd, idx) => {
            const fwdStepId = fwd.step_id;
            if (!fwdStepId) return;
            const fwdId = `node-${fwdStepId}`;

            const nextIds = getNextStepIds(fwd);
            nextIds.forEach((targetId, nIdx) => {
                const targetNodeId = findNodeIdByStepId(targetId, steps, f.forwarders || []);
                if (targetNodeId) {
                    edgeList.push({ id: `edge-${fwdId}-${targetNodeId}-${nIdx}`, from: fwdId, to: targetNodeId, fromSide: 'bottom', toSide: 'top', manual: true });
                }
            });
        });

        // 5. No orphan reconnection.
        // Orphaned nodes (no outgoing edge) are NOT auto-connected to the end
        // node. If a node has no edge, it is not part of the workflow — the form
        // execution stops at that node (FLOW_END). The user must explicitly draw
        // an edge to connect it to the rest of the workflow.

        // 6. Apply dagre layout only if NO stored positions exist anywhere.
        // Check both step.canvas and canvas_meta (for start/end/fwd/rd nodes).
        // This prevents dagre from destroying manual layout on return from editing.
        const hasStoredPositions = steps.some(s => s.canvas) ||
            Object.keys(f.canvas_meta || {}).length > 0;
        if (!hasStoredPositions && nodeList.length > 2) {
            const laidOut = computeDagreLayout(nodeList, edgeList);
            setNodes(laidOut);
        } else {
            setNodes(nodeList);
        }
        setEdges(edgeList);
    };

    // ── Build save payload with node positions written through ──────────────
    // Centralized so handleSave, handlePreview, and openNodeEditor all
    // persist canvas positions consistently. Previously, openNodeEditor and
    // handlePreview saved with a stripped payload that discarded all node
    // positions. On return to the canvas, buildNodesFromForm found no stored
    // positions and re-ran dagre, destroying manual layout.
    const buildSavePayload = () => {
        // Write step node titles + positions back to form data
        const updatedSteps = (form.steps || []).map(s => {
            const node = nodes.find(n => n.stepId === s.step_id);
            if (!node) return s;
            return {
                ...s,
                title: node.title !== s.title ? node.title : s.title,
                canvas: { x: Math.round(node.x), y: Math.round(node.y) },
            };
        });

        // Write forwarder titles
        // Write forwarder titles — find by stepId (not forwarderIndex, which goes stale after deletion)
        const updatedForwarders = forwarders.map((fwd) => {
            const node = nodes.find(n => n.type === 'forwarder' && n.stepId === fwd.step_id);
            return node && node.title !== fwd.name ? { ...fwd, name: node.title } : fwd;
        });

        // Store non-step node positions (start, end, forwarder, redirect) in canvas_meta
        const canvasMeta = {};
        nodes.forEach(n => {
            if (n.id === 'node-start' || n.id === 'node-end' || n.type === 'forwarder' || n.type === 'redirect') {
                canvasMeta[n.id] = { x: Math.round(n.x), y: Math.round(n.y) };
            }
        });

        // Auto-enable per-step forwarders when any enabled forwarder is attached
        // to a step. A forwarder is "step-attached" when its node ID (forwarder.step_id,
        // e.g. "fwd_1786926197559") appears in some step's next_step_id array.
        //
        // IMPORTANT: forwarder.step_id is the forwarder's OWN node ID in the canvas,
        // NOT the ID of the step it belongs to. The step attachment is encoded in the
        // step's next_step_id array. This check must mirror the backend's matching
        // logic in routes/forms.py (create_form / update_form guards, per-step submit,
        // terminal submit filter, and form_workflow_run.finalize_run).
        //
        // Do NOT simplify this to `f.step_id` (truthy check) — every forwarder has a
        // step_id (its own node ID), so that would always be true and would
        // auto-enable per-step mode even for forms with only terminal forwarders.
        const allNextIds = new Set();
        updatedSteps.forEach(s => {
            const nextIds = getNextStepIds(s);
            nextIds.forEach(id => allNextIds.add(id));
        });
        const hasStepForwarders = updatedForwarders.some(
            f => f.enabled !== false && f.step_id && allNextIds.has(f.step_id)
        );
        const currentSettings = form.settings || {};
        const settings = hasStepForwarders
            ? { ...currentSettings, enable_per_step_forwarders: true }
            : { ...currentSettings, enable_per_step_forwarders: false };

        // Final normalization so the document the server stores is exactly
        // the graph the canvas shows (the server applies the same rules).
        const graph = normalizeWorkflowGraph({ ...form, steps: updatedSteps, forwarders: updatedForwarders });

        return {
            ...form,
            steps: graph.steps,
            forwarders: graph.forwarders,
            entry_step_id: graph.entry_step_id,
            canvas_meta: canvasMeta,
            settings,
            status: form.status || 'draft',
        };
    };

    // Author-facing warnings shown after a successful save.
    const warnAboutGraph = (payload) => {
        const entry = resolveEntryStepId(payload);
        if (!entry) {
            toast.warning('The Start node is not connected — the workflow has no entry point.');
            return;
        }
        const reach = computeReachableIds(payload.steps || [], payload.forwarders || [], entry);
        const orphanSteps = (payload.steps || []).filter(s => !reach.has(s.step_id));
        const orphanFwds = (payload.forwarders || []).filter(f => f.step_id && !reach.has(f.step_id));
        const n = orphanSteps.length + orphanFwds.length;
        if (n > 0) {
            toast.warning(`${n} node${n === 1 ? ' is' : 's are'} not connected to the flow and will be skipped.`);
        }
    };

    // ── Save (Issue 10: title write-through + position persistence) ──────────
    const handleSave = async () => {
        if (!form?.name?.trim()) { toast.error('Form name required'); return; }
        setSaving(true);
        try {
            const payload = buildSavePayload();
            if (isNew) {
                const res = await axios.post(`${API}/forms`, payload, { headers: getAuthHeader() });
                toast.success('Workflow created');
                setIsDirty(false);
                navigate(`/forms/${res.data.form_id}/workflow`, { replace: true });
                setForm(res.data);
            } else {
                const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
                toast.success('Workflow saved');
                setIsDirty(false);
                setForm(keepRegistrationEvent(res.data, form));
                setSavedRegistrationId(registrationIdOf(res.data));
            }
            warnAboutGraph(payload);
        } catch (err) { toast.error(errorText(err, 'Failed to save')); }
        finally { setSaving(false); }
    };

    // ── Publish / Unpublish ──────────────────────────────────────────────
    const handlePublish = async () => {
        if (isNew) { toast.error('Save the workflow first'); return; }
        setPublishing(true);
        try {
            // Publish what the canvas shows: unsaved edits (an event picked in Form Settings among them) are saved first,
            // the way Preview saves, instead of being dropped by the reload below (#919 QA round 2, owner decision
            // 2026-10-26). A refused save stops here with the server's sentence.
            if (isDirty) {
                const payload = buildSavePayload();
                let saved;
                try {
                    saved = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
                } catch (err) {
                    toast.error(errorText(err, 'Failed to save before publishing'));
                    return;
                }
                setIsDirty(false);
                setForm(keepRegistrationEvent(saved.data, form));
                setSavedRegistrationId(registrationIdOf(saved.data));
                warnAboutGraph(payload);
            }
            const isPublished = form?.status === 'published';
            const endpoint = isPublished ? 'unpublish' : 'publish';
            await axios.post(`${API}/forms/${formId}/${endpoint}`, {}, { headers: getAuthHeader() });
            toast.success(isPublished ? 'Workflow unpublished' : 'Workflow published');
            const res = await axios.get(`${API}/forms/${formId}`, { headers: getAuthHeader() });
            setForm(res.data);
            setSavedRegistrationId(registrationIdOf(res.data));
        } catch (err) { toast.error(errorText(err, 'Failed to publish')); }
        finally { setPublishing(false); }
    };

    // ── Preview ──────────────────────────────────────────────────────────
    const handlePreview = async () => {
        if (isNew) { toast.error('Save the workflow first'); return; }
        setSaving(true);
        try {
            const payload = buildSavePayload();
            const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
            setIsDirty(false);
            setForm(keepRegistrationEvent(res.data, form));
            setSavedRegistrationId(registrationIdOf(res.data));
            warnAboutGraph(payload);
            const slug = form?.slug;
            if (slug) {
                window.open(`/form/${slug}`, '_blank');
            } else {
                toast.error('No slug found — save and publish first');
            }
        } catch (err) {
            toast.error(errorText(err, 'Failed to save before preview'));
        } finally {
            setSaving(false);
        }
    };

    // ── Format Nodes (Issue 11) ──────────────────────────────────────────
    const handleFormatNodes = () => {
        const laidOut = computeDagreLayout(nodes, edges);
        setNodes(laidOut);
        markDirty();
        toast.success('Nodes formatted');
    };

    // ── Node operations ───────────────────────────────────────────────────
    const addNode = (type) => {
        const id = `node-${Date.now()}`;
        const cfg = NODE_TYPES[type];
        const yPos = nodes.length > 0 ? Math.max(...nodes.map(n => n.y)) + 150 : 170;
        const newNode = { id, type, title: cfg?.label || 'New Node', meta: cfg?.desc || '', x: 310, y: yPos };

        if (type === 'form' || type === 'custom') {
            const stepId = `step_${Date.now()}`;
            const kind = type === 'custom' ? 'splash' : 'question';
            const newStep = {
                step_id: stepId,
                kind,
                title: cfg?.label || 'New Step',
                fields: [],
                ...(type === 'custom' ? { splash: { page_type: 'thank_you', blocks: [] } } : {}),
            };
            const currentSteps = form?.steps || [];
            setForm({ ...form, steps: [...currentSteps, newStep] });
            newNode.stepId = stepId;
            newNode.stepIndex = currentSteps.length;
        }

        if (type === 'condition') {
            const stepId = `step_${Date.now()}`;
            const newStep = {
                step_id: stepId,
                kind: 'question',
                title: 'Condition',
                fields: [],
                branch: {
                    mode: 'switch_case',
                    source_node: '',
                    source_field: '',
                    cases: [],
                    branches: [],
                    default_target: '',
                },
            };
            const currentSteps = form?.steps || [];
            setForm({ ...form, steps: [...currentSteps, newStep] });
            newNode.stepId = stepId;
            newNode.stepIndex = currentSteps.length;
            newNode.meta = '0 branches · Switch Case';
        }

        if (type === 'redirect') {
            const stepId = `step_${Date.now()}`;
            const newStep = {
                step_id: stepId,
                kind: 'redirect',
                title: `Redirect ${form?.steps?.filter(s => s.kind === 'redirect').length + 1 || 1}`,
                redirect_url: '',
                trigger_submit: false,
                query_params: [],
            };
            const currentSteps = form?.steps || [];
            setForm({ ...form, steps: [...currentSteps, newStep] });
            newNode.stepId = stepId;
            newNode.meta = 'URL redirect';
        }

        if (type === 'forwarder') {
            const fwdStepId = `fwd_${Date.now()}`;
            const defaultForwarder = {
                step_id: fwdStepId,
                name: `Forwarder ${forwarders.length + 1}`,
                type: 'gohighlevel',
                enabled: true,
                method: 'POST',
                url: '',
                auth: { type: 'pit', token: '', location_id: '' },
                payload_template: '',
                ghl_field_mappings: [],
                send_utm: false,
            };
            setForwarders([...forwarders, defaultForwarder]);
            newNode.stepId = fwdStepId;
        }

        if (type === 'end') {
            const stepId = `step_${Date.now()}`;
            const newStep = {
                step_id: stepId,
                kind: 'end',
                title: 'End',
            };
            const currentSteps = form?.steps || [];
            setForm({ ...form, steps: [...currentSteps, newStep] });
            newNode.stepId = stepId;
            newNode.stepIndex = currentSteps.length;
            newNode.meta = 'Workflow exit';
        }

        // First step node of a workflow with no entry yet: connect Start to it
        // so the entry pointer is set without a manual drag.
        const newEdges = [];
        if (newNode.stepId && type !== 'end' && type !== 'forwarder'
            && !resolveEntryStepId(form) && (form?.steps || []).length === 0) {
            setForm(prev => ({ ...prev, entry_step_id: newNode.stepId }));
            newEdges.push({ id: `edge-start-${id}`, from: 'node-start', to: id, fromSide: 'bottom', toSide: 'top', entry: true });
        }

        setNodes([...nodes, newNode]);
        if (newEdges.length) setEdges([...edges, ...newEdges]);
        setSelectedNodeId(id);
        markDirty();
    };

    const deleteNode = (nodeId) => {
        const node = nodes.find(n => n.id === nodeId);
        if (node?.stepId) {
            // Remove the node AND every reference to it (other nodes'
            // next_step_id, branch targets, correlation sources, the entry
            // pointer). A dangling id used to survive here, sit first in the
            // predecessor's next_step_id and end the flow at runtime.
            const purged = purgeNodeReferences({ ...form, forwarders }, node.stepId);
            setForm(prev => ({ ...prev, steps: purged.steps, forwarders: purged.forwarders, entry_step_id: purged.entry_step_id }));
            setForwarders(purged.forwarders);
        }
        setNodes(nodes.filter(n => n.id !== nodeId));
        setEdges(edges.filter(e => e.from !== nodeId && e.to !== nodeId));
        if (selectedNodeId === nodeId) setSelectedNodeId(null);
        markDirty();
    };

    // ── Canvas pan ────────────────────────────────────────────────────────────
    const onCanvasBgMouseDown = (e) => {
        if (e.target.classList.contains('wf-canvas-area') || e.target.classList.contains('wf-canvas-inner') || e.target.tagName === 'svg') {
            setPanning({ startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y });
            setSelectedNodeId(null);
            setSelectedEdgeId(null);
        }
    };

    // ── Node drag ──────────────────────────────────────────────────────────
    const onNodeMouseDown = (e, nodeId) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target.classList.contains('wf-port')) return;
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return;
        const rect = canvasInnerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setDrag({
            kind: 'node',
            id: nodeId,
            offsetX: (e.clientX - rect.left) / zoom - node.x,
            offsetY: (e.clientY - rect.top) / zoom - node.y,
        });
        setSelectedNodeId(nodeId);
        setSelectedEdgeId(null);
        setShowSettings(false);
        markDirty();
    };

    const onCanvasMouseMove = (e) => {
        if (panning) {
            setPan({ x: panning.panX + (e.clientX - panning.startX), y: panning.panY + (e.clientY - panning.startY) });
            return;
        }
        if (drag?.kind === 'node') {
            const rect = canvasInnerRef.current?.getBoundingClientRect();
            if (!rect) return;
            // Clamp within canvas bounds so nodes can't be dragged off any edge
            const rawX = (e.clientX - rect.left - drag.offsetX) / zoom;
            const rawY = (e.clientY - rect.top - drag.offsetY) / zoom;
            const newX = Math.max(0, Math.min(CANVAS_WIDTH - NODE_W, rawX));
            const newY = Math.max(0, rawY);
            setNodes(nodes.map(n => n.id === drag.id ? { ...n, x: newX, y: newY } : n));
        }
        if (connecting) {
            const rect = canvasInnerRef.current?.getBoundingClientRect();
            if (!rect) return;
            setConnecting({ ...connecting, mouseX: (e.clientX - rect.left) / zoom, mouseY: (e.clientY - rect.top) / zoom });
        }
    };

    const onCanvasMouseUp = () => {
        setDrag(null);
        if (connecting) setConnecting(null);
        if (panning) setPanning(null);
    };

    // ── Port connection drag ──────────────────────────────────────────────
    // Enforce connector type: top = INPUT, bottom/left/right = OUTPUT.
    // Only OUTPUT → INPUT connections are allowed.
    //   - Drag must START from an OUTPUT port (bottom/left/right)
    //   - Drag must END on an INPUT port (top)
    // This prevents input↔input, output↔output, and reversed input→output.
    const onPortMouseDown = (e, nodeId, side) => {
        // Reject drag from INPUT port (top) — input ports receive, they don't initiate
        if (side === 'top') return;
        e.preventDefault();
        e.stopPropagation();
        // Condition nodes route through their branch config only. A hand-drawn
        // edge out of one was never persisted and vanished on reload.
        const fromNode = nodes.find(n => n.id === nodeId);
        if (fromNode?.type === 'condition') {
            toast.error('Condition branches are configured in the Condition editor (Edit Node).');
            return;
        }
        const rect = canvasInnerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setConnecting({ fromNode: nodeId, fromSide: side, mouseX: (e.clientX - rect.left) / zoom, mouseY: (e.clientY - rect.top) / zoom });
    };

    const onPortMouseUp = (e, nodeId, side) => {
        e.preventDefault();
        e.stopPropagation();
        if (!connecting) return;
        // Reject drop on OUTPUT port — output ports initiate, they don't receive
        if (side !== 'top') {
            setConnecting(null);
            return;
        }
        // Reject self-connection and anything INTO the Start node
        if (connecting.fromNode === nodeId || nodeId === 'node-start') {
            setConnecting(null);
            return;
        }
        const fromNode = nodes.find(n => n.id === connecting.fromNode);
        const toNode = nodes.find(n => n.id === nodeId);
        const isDuplicate = edges.some(ed => ed.from === connecting.fromNode && ed.to === nodeId);
        const newEdge = {
            id: `edge-${connecting.fromNode}-${nodeId}-${Date.now()}`,
            from: connecting.fromNode, to: nodeId,
            fromSide: connecting.fromSide, toSide: side,
            manual: true, // user-drawn edges keep their explicit sides
        };

        // ── Start → step: persisted as form.entry_step_id ──
        if (fromNode?.id === 'node-start') {
            if (!toNode?.stepId || !isValidEntryTarget(toNode.stepId, form?.steps || [], forwarders)) {
                toast.error('The Start node must connect to a Form page, Custom page, Condition or Redirect.');
                setConnecting(null);
                return;
            }
            if (!isDuplicate) {
                // Exactly one entry: replace any existing Start edge.
                setEdges([...edges.filter(ed => ed.from !== 'node-start'), { ...newEdge, entry: true }]);
                setForm(prev => ({ ...prev, entry_step_id: toNode.stepId }));
                markDirty();
            }
            setConnecting(null);
            return;
        }

        // ── Step / forwarder → target: persisted via next_step_id ──
        // EDGE RULE: one step target per node (form / custom / condition /
        // redirect / end) plus any number of forwarders. Drawing a second step
        // edge replaces the first. Condition sources are rejected at mousedown.
        if (shouldPersistManualEdge(fromNode, toNode) && !isDuplicate) {
            const targetIsForwarder = toNode.type === 'forwarder' || isForwarderId(toNode.stepId, forwarders);
            let nextEdges = edges;
            if (!targetIsForwarder) {
                // Drop the previous step edge from this source (keep forwarder edges).
                nextEdges = edges.filter(ed => {
                    if (ed.from !== fromNode.id) return true;
                    const tgt = nodes.find(n => n.id === ed.to);
                    return tgt?.type === 'forwarder';
                });
                if (nextEdges.length !== edges.length) {
                    toast.info('Replaced the previous connection — a node can lead to only one step.');
                }
            }
            setEdges([...nextEdges, newEdge]);

            if (fromNode.type === 'forwarder') {
                setForwarders(prev => replaceStepTarget(prev, fromNode.stepId, toNode.stepId, prev).items);
            } else {
                setForm(prevForm => {
                    if (!prevForm?.steps) return prevForm;
                    return { ...prevForm, steps: replaceStepTarget(prevForm.steps, fromNode.stepId, toNode.stepId, forwarders).items };
                });
            }
            markDirty();
        } else if (!isDuplicate && !shouldPersistManualEdge(fromNode, toNode)) {
            // Nothing would persist this edge (e.g. target without a step id) — refuse
            // rather than draw a line that disappears on reload.
            toast.error('That connection cannot be saved.');
        }

        setConnecting(null);
    };

    // ── Edge operations ───────────────────────────────────────────────────
    const deleteEdge = (edgeId) => {
        const edge = edges.find(e => e.id === edgeId);
        // Start edge → clear the entry pointer (explicit null: "disconnected",
        // distinct from a legacy document that never had the key).
        if (edge && edge.from === 'node-start') {
            setForm(prev => ({ ...prev, entry_step_id: null }));
        }
        // Branch-derived condition edge → clear that case / branch / default
        // target so the deletion persists (it used to reappear on reload).
        if (edge && edge.branchRef) {
            const { stepId, kind, idx } = edge.branchRef;
            setForm(prev => ({
                ...prev,
                steps: (prev?.steps || []).map(s => {
                    if (s.step_id !== stepId || !s.branch) return s;
                    const branch = { ...s.branch };
                    if (kind === 'case') branch.cases = (branch.cases || []).map((c, i) => i === idx ? { ...c, targetStepId: '' } : c);
                    if (kind === 'branch') branch.branches = (branch.branches || []).map((b, i) => i === idx ? { ...b, targetStepId: '' } : b);
                    if (kind === 'default') branch.default_target = '';
                    return { ...s, branch };
                }),
            }));
        }
        // Clear persistence if this was a manual edge
        if (edge && edge.manual) {
            const fromNode = nodes.find(n => n.id === edge.from);
            const toNode = nodes.find(n => n.id === edge.to);
            if (fromNode?.type === 'forwarder') {
                // Remove only the specific target from the forwarder's next_step_id array
                // F25: Use step_id matching instead of forwarderIndex (stale after deletion)
                setForwarders(prev =>
                    prev.map((fwd) => {
                        if (fwd.step_id !== fromNode.stepId) return fwd;
                        const existing = getNextStepIds(fwd);
                        const filtered = existing.filter(id => id !== toNode?.stepId);
                        const copy = { ...fwd };
                        if (filtered.length === 0) {
                            delete copy.next_step_id;
                        } else {
                            copy.next_step_id = filtered;
                        }
                        return copy;
                    })
                );
            } else if (isNonConditionNode(fromNode)) {
                // Remove only the specific target from the step's next_step_id array
                setForm(prevForm => {
                    if (!prevForm?.steps) return prevForm;
                    return { ...prevForm, steps: clearStepNextStepId(prevForm.steps, fromNode.stepId, toNode?.stepId) };
                });
            }
        }
        setEdges(edges.filter(e => e.id !== edgeId));
        setSelectedEdgeId(null);
        markDirty();
    };

    // ── Open editors based on node type ───────────────────────────────────
    const openNodeEditor = async (node) => {
        // Save current canvas positions before navigating away.
        // All node types use the same buildSavePayload() so positions are
        // persisted consistently — previously this used a stripped payload
        // that discarded all canvas positions, causing buildNodesFromForm to
        // re-run dagre and destroy manual layout on return.
        if (!isNew) {
            setSaving(true);
            try {
                const payload = buildSavePayload();
                const res = await axios.put(`${API}/forms/${formId}`, payload, { headers: getAuthHeader() });
                setForm(keepRegistrationEvent(res.data, form));
                setSavedRegistrationId(registrationIdOf(res.data));
                setIsDirty(false);
            } catch (err) {
                toast.error(errorText(err, 'Failed to save before editing node'));
                setSaving(false);
                return;
            }
            setSaving(false);
        }

        const stepId = node.stepId || '';
        if (node.type === 'form') {
            navigate(`/forms/${formId}/edit${stepId ? `?step=${stepId}` : ''}`);
        } else if (node.type === 'custom') {
            navigate(`/forms/${formId}/steps/${stepId}/design`);
        } else if (node.type === 'condition') {
            navigate(`/forms/${formId}/conditions/${stepId}`);
        } else if (node.type === 'forwarder') {
            navigate(`/forms/${formId}/forwarders?step=${stepId}`);
        }
        // Redirect nodes are configured via the properties panel — no dedicated editor
    };

    const selectedNode = nodes.find(n => n.id === selectedNodeId);

    // ── Edge path calculation ──────────────────────────────────────────────────
    const getPortPosition = (node, side) => {
        switch (side) {
            case 'top':    return { x: node.x + NODE_W / 2, y: node.y - 7 };
            case 'right':  return { x: node.x + NODE_W + 7, y: node.y + NODE_H / 2 };
            case 'bottom': return { x: node.x + NODE_W / 2, y: node.y + NODE_H + 7 };
            case 'left':   return { x: node.x - 7,           y: node.y + NODE_H / 2 };
            default:       return null;
        }
    };

    // ── Compute nearest OUTPUT port for an edge ───────────────────────────────
    // Mental model: top = INPUT (incoming), bottom/left/right = OUTPUT (outgoing).
    // This picks the output port closest to the target so edges look natural.
    const computeFromSide = (fromNode, toNode) => {
        const fromCx = fromNode.x + NODE_W / 2;
        const fromCy = fromNode.y + NODE_H / 2;
        const toCx = toNode.x + NODE_W / 2;
        const toCy = toNode.y + NODE_H / 2;
        const dx = toCx - fromCx;
        const dy = toCy - fromCy;
        // If the target is more horizontal than vertical, use a side port
        if (Math.abs(dx) > Math.abs(dy)) {
            return dx > 0 ? 'right' : 'left';
        }
        // Default: bottom (the primary output port)
        return 'bottom';
    };

    const getEdgePath = (edge) => {
        const fromNode = nodes.find(n => n.id === edge.from);
        const toNode = nodes.find(n => n.id === edge.to);
        if (!fromNode || !toNode) return '';
        // Always compute fromSide dynamically so edges follow the nearest OUTPUT
        // port as nodes are dragged or dagre-laid-out. toSide is always 'top'
        // (the INPUT port) unless this is a manually-drawn edge with an explicit
        // toSide.
        const fromSide = computeFromSide(fromNode, toNode);
        const toSide = edge.manual ? edge.toSide : 'top';
        const fromPos = getPortPosition(fromNode, fromSide);
        const toPos = getPortPosition(toNode, toSide);
        if (!fromPos || !toPos) return '';
        const dx = toPos.x - fromPos.x;
        const cp1x = fromPos.x + dx * 0.5;
        const cp2x = toPos.x - dx * 0.5;
        return `M ${fromPos.x} ${fromPos.y} C ${cp1x} ${fromPos.y}, ${cp2x} ${toPos.y}, ${toPos.x} ${toPos.y}`;
    };

    // ── Edge label midpoint (Issue 9) ──────────────────────────────────────────
    const getEdgeMidpoint = (edge) => {
        const fromNode = nodes.find(n => n.id === edge.from);
        const toNode = nodes.find(n => n.id === edge.to);
        if (!fromNode || !toNode) return null;
        const fromSide = computeFromSide(fromNode, toNode);
        const toSide = edge.manual ? edge.toSide : 'top';
        const fromPos = getPortPosition(fromNode, fromSide);
        const toPos = getPortPosition(toNode, toSide);
        if (!fromPos || !toPos) return null;
        return { x: (fromPos.x + toPos.x) / 2, y: (fromPos.y + toPos.y) / 2 };
    };

    if (loading) {
        return (
            <div className="flex flex-1 items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <>
        <style>{`
            .wf-sub-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; border-bottom: 1px solid #E8E5DF; background: #FFFFFF; flex-shrink: 0; gap: 12px; }
            .wf-sub-header-left { display: flex; align-items: center; gap: 12px; }
            .wf-sub-header h2 { font-size: 16px; font-weight: 700; color: #1C1917; }
            .wf-sub-header-actions { display: flex; gap: 8px; }
            .wf-layout { display: flex; flex: 1; overflow: hidden; }
            .wf-palette { width: 220px; background: #FFFFFF; border-right: 1px solid #E8E5DF; padding: 16px; overflow-y: auto; flex-shrink: 0; }
            .wf-palette-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #6E6961; margin-bottom: 12px; }
            .wf-palette-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px; border-radius: 8px; border: 1px solid #E8E5DF; margin-bottom: 8px; cursor: grab; transition: background 150ms ease; }
            .wf-palette-item:hover { background: #F1F0EB; }
            .wf-palette-info { flex: 1; min-width: 0; }
            .wf-palette-name { font-size: 13px; font-weight: 600; color: #1C1917; }
            .wf-palette-desc { font-size: 11px; color: #A29D96; }
            .wf-canvas-area { flex: 1; overflow: hidden; background: #F7F6F2; background-image: radial-gradient(circle, #D6D3D1 1px, transparent 1px); background-size: 20px 20px; position: relative; cursor: grab; }
            .wf-canvas-area.panning { cursor: grabbing; }
            .wf-canvas-inner { width: ${CANVAS_WIDTH}px; height: 100%; position: relative; margin: 0 auto; }
            .wf-canvas-edges { position: absolute; top: 0; left: 0; width: ${CANVAS_WIDTH}px; height: 100%; pointer-events: none; overflow: visible; }
            .wf-canvas-edges .edge-hit { stroke: transparent; stroke-width: 20; fill: none; cursor: pointer; pointer-events: stroke; }
            .wf-canvas-edges .edge-line { stroke: #A29D96; stroke-width: 2; fill: none; transition: stroke 150ms ease, stroke-width 150ms ease; pointer-events: none; }
            .wf-canvas-edges .edge-line.selected { stroke: #6366F1; stroke-width: 3; }
            .wf-canvas-edges .edge-arrow { pointer-events: none; }
            .wf-canvas-edges .edge-delete-btn { pointer-events: all; cursor: pointer; }
            .wf-canvas-edges .temp-edge { stroke: #6366F1; stroke-width: 2; stroke-dasharray: 4 4; fill: none; pointer-events: none; }
            .wf-canvas-edges .edge-label-bg { fill: var(--surface, #FFFFFF); stroke: #E8E5DF; stroke-width: 1; }
            .wf-canvas-edges .edge-label-text { font-size: 10px; fill: #57534E; font-weight: 600; pointer-events: none; }
            .wf-canvas-help { position: absolute; bottom: 12px; left: 12px; z-index: 10; background: rgba(99,102,241,0.06); border: 1px solid rgba(99,102,241,0.2); border-radius: 6px; padding: 6px 12px; font-size: 11px; color: #78716C; display: flex; align-items: center; gap: 6px; pointer-events: none; max-width: 320px; }
            .wf-zoom-controls { position: absolute; top: 12px; right: 12px; z-index: 10; display: flex; flex-direction: column; gap: 4px; background: #FFFFFF; border: 1px solid #E8E5DF; border-radius: 8px; padding: 4px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
            .wf-zoom-controls button { width: 32px; height: 32px; border-radius: 6px; border: none; background: transparent; color: #57534E; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 150ms ease; }
            .wf-zoom-controls button:hover { background: #F1F0EB; color: #1C1917; }
            .wf-node { position: absolute; width: ${NODE_W}px; background: #FFFFFF; border: 1.5px solid #E8E5DF; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.04); padding: 10px 12px; cursor: grab; transition: box-shadow 150ms ease, border-color 150ms ease; user-select: none; }
            .wf-node:hover { box-shadow: 0 4px 6px rgba(0,0,0,0.07); border-color: #A29D96; }
            .wf-node.selected { border-color: #6366F1; box-shadow: 0 0 0 3px rgba(99,102,241,0.2); }
            .wf-node.dragging { opacity: 0.8; cursor: grabbing !important; z-index: 100; }
            .wf-node-delete { position: absolute; top: -8px; right: -8px; width: 20px; height: 20px; border-radius: 50%; background: #C2363F; color: #FFFFFF; border: 2px solid #FFFFFF; display: none; align-items: center; justify-content: center; cursor: pointer; z-index: 20; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
            .wf-node:hover .wf-node-delete, .wf-node.selected .wf-node-delete { display: flex; }
            .wf-node-delete:hover { background: #A02A30; }
            .wf-node-top { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
            .wf-node-type { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
            .wf-node-title { font-size: 13px; font-weight: 700; color: #1C1917; }
            .wf-node-meta { font-size: 11px; color: #A29D96; margin-top: 2px; }
            .wf-port { position: absolute; width: 12px; height: 12px; border-radius: 50%; background: #FFFFFF; border: 2px solid #A29D96; cursor: crosshair; transition: all 150ms ease; z-index: 5; }
            /* INPUT ports (top) don't initiate drags — show not-allowed by default, pointer when a drag is active */
            .wf-port-input { cursor: not-allowed; }
            .wf-port-input.connectable { cursor: pointer; }
            .wf-port:hover { width: 16px; height: 16px; border-color: #6366F1; background: rgba(99,102,241,0.12); box-shadow: 0 0 0 4px rgba(99,102,241,0.15); }
            .wf-port.connectable { border-color: #6366F1; background: rgba(99,102,241,0.1); }
            /* When dragging from an OUTPUT port, highlight all INPUT ports (top) as valid drop targets */
            .wf-port-input.connectable { border-color: #1A7F4E; background: #1A7F4E; box-shadow: 0 0 0 4px rgba(26,127,78,0.2); animation: wf-pulse 1s ease-in-out infinite; }
            @keyframes wf-pulse { 0%, 100% { box-shadow: 0 0 0 4px rgba(26,127,78,0.2); } 50% { box-shadow: 0 0 0 6px rgba(26,127,78,0.35); } }
            /* Input ports (top) — filled green to signal "data comes in here" */
            .wf-port-input { border-color: #1A7F4E; background: #1A7F4E; }
            .wf-port-input:hover { border-color: #1A7F4E; background: #1A7F4E; box-shadow: 0 0 0 4px rgba(26,127,78,0.15); }
            /* Output ports (bottom/left/right) — hollow amber to signal "data goes out here" */
            .wf-port-output { border-color: #B45309; background: #FFFFFF; }
            .wf-port-output:hover { border-color: #B45309; background: rgba(180,83,9,0.1); box-shadow: 0 0 0 4px rgba(180,83,9,0.12); }
            .wf-port-disabled, .wf-port-disabled:hover { cursor: not-allowed; border-color: #D6D3D1; background: #F5F5F4; box-shadow: none; }
            .wf-node-warn { display: inline-block; margin-top: 4px; padding: 2px 8px; border-radius: 4px; background: #FCEFDD; color: #B45309; font-size: 10px; font-weight: 600; border: 1px solid #FED7AA; }
            .wf-port-top { top: -7px; left: 50%; transform: translateX(-50%); }
            .wf-port-right { right: -7px; top: 50%; transform: translateY(-50%); }
            .wf-port-bottom { bottom: -7px; left: 50%; transform: translateX(-50%); }
            .wf-port-left { left: -7px; top: 50%; transform: translateY(-50%); }
            .wf-props-panel { width: 280px; background: #FFFFFF; border-left: 1px solid #E8E5DF; padding: 16px; overflow-y: auto; flex-shrink: 0; }
            .wf-props-title { font-size: 14px; font-weight: 700; margin-bottom: 12px; color: #1C1917; }
            .wf-props-row { margin-bottom: 12px; }
            .wf-props-label { display: block; font-size: 11px; font-weight: 600; color: #6E6961; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.04em; }
            .wf-props-input { width: 100%; padding: 8px 10px; border: 1px solid #E8E5DF; border-radius: 6px; font-size: 13px; color: #1C1917; background: #FFFFFF; }
            .wf-props-input:focus { outline: none; border-color: #1C1917; }
            .wf-btn-outline { padding: 6px 12px; border-radius: 6px; border: 1px solid #E8E5DF; font-size: 13px; font-weight: 500; color: #57534E; background: #FFFFFF; cursor: pointer; display: flex; align-items: center; gap: 4px; }
            .wf-btn-outline:hover { background: #F1F0EB; }
            .wf-btn-primary { padding: 6px 12px; border-radius: 6px; background: #1C1917; color: #F7F6F2; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 4px; }
            .wf-btn-primary:hover { background: #292524; }
            .wf-btn-add-fwd { width: 100%; margin-top: 12px; padding: 8px 12px; border-radius: 8px; background: #1C1917; color: #FFFFFF; font-size: 13px; font-weight: 500; display: flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer; }
            .wf-btn-add-fwd:hover { background: #292524; }
            .wf-root { flex: 1; min-height: 0; }
        `}</style>
        <div className="wf-root flex flex-col bg-stone-50">
            {/* Sub-header */}
            <div className="wf-sub-header">
                <div className="wf-sub-header-left">
                    <button onClick={() => blockedNavigate('/forms')} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100" title="Back to Forms">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h2>{form?.name || 'Untitled Workflow'}</h2>
                    {registration && (
                        // Opens Form Settings in place, where the Event registration section is: nothing to leave, so
                        // unsaved canvas edits stay where they are.
                        <button
                            type="button"
                            data-testid="registration-chip"
                            onClick={() => setShowSettings(true)}
                            title="Open this form's event registration settings"
                            className={`inline-flex max-w-[280px] items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                                registration.unavailable
                                    ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
                                    : 'border-stone-200 bg-stone-50 text-stone-700 hover:bg-stone-100'
                            }`}
                        >
                            <span className="truncate">
                                {registration.unavailable ? 'Registers on: event unavailable' : `Registers on: ${registration.name}`}
                            </span>
                        </button>
                    )}
                </div>
                <div className="wf-sub-header-actions">
                    <button className="wf-btn-outline" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                    </button>
                    {!isNew && selectedNode && selectedNode.type !== 'start' && selectedNode.type !== 'end' && selectedNode.type !== 'redirect' && (
                        <button className="wf-btn-primary" onClick={() => openNodeEditor(selectedNode)}>
                            <Settings className="w-3.5 h-3.5" />
                            Edit Node
                        </button>
                    )}
                    {/* Format Nodes button (Issue 11) — between Edit Node and Preview */}
                    {!isNew && (
                        <button className="wf-btn-outline" onClick={handleFormatNodes} title="Auto-arrange nodes">
                            <LayoutGrid className="w-3.5 h-3.5" />
                            Format Nodes
                        </button>
                    )}
                    {!isNew && (
                        <button className="wf-btn-outline" onClick={() => setShowSettings(s => !s)} title="Form settings">
                            <Settings className="w-3.5 h-3.5" />
                            Settings
                        </button>
                    )}
                    {!isNew && (
                        <button className="wf-btn-outline" onClick={handlePreview} disabled={saving}>
                            <Eye className="w-3.5 h-3.5" />
                            Preview
                        </button>
                    )}
                    {!isNew && (
                        <button className="wf-btn-primary" onClick={handlePublish} disabled={publishing}>
                            {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                            {form?.status === 'published' ? 'Unpublish' : 'Publish'}
                        </button>
                    )}
                </div>
            </div>

            {/* Workflow layout */}
            <div className="wf-layout">
                {/* Node Palette */}
                <div className="wf-palette">
                    <div className="wf-palette-title">Add Node</div>
                    {PALETTE_ITEMS.map(item => {
                        const cfg = NODE_TYPES[item.type];
                        return (
                            <div key={item.type} className="wf-palette-item" onClick={() => addNode(item.type)}>
                                <PaletteIcon type={item.type} />
                                <div className="wf-palette-info">
                                    <div className="wf-palette-name">{item.name}</div>
                                    <div className="wf-palette-desc">{item.desc}</div>
                                </div>
                            </div>
                        );
                    })}
                    <button className="wf-btn-add-fwd" onClick={() => blockedNavigate(`/forms/${formId}/forwarders`)}>
                        <Plus className="w-4 h-4" />
                        Manage Forwarders
                    </button>
                </div>

                {/* Canvas */}
                <div
                    ref={attachCanvasWheel}
                    className={`wf-canvas-area ${panning ? 'panning' : ''}`}
                    onMouseDown={onCanvasBgMouseDown}
                    onMouseMove={onCanvasMouseMove}
                    onMouseUp={onCanvasMouseUp}
                    onMouseLeave={onCanvasMouseUp}
                >
                    <div className="wf-canvas-help">
                        💡 Drag nodes to reposition · Drag from ports to connect · Drag background to pan · Click an edge then ✕ to delete
                    </div>

                    <div className="wf-zoom-controls">
                        <button onClick={handleZoomIn} title="Zoom in"><ZoomIn className="w-4 h-4" /></button>
                        <button onClick={handleZoomOut} title="Zoom out"><ZoomOut className="w-4 h-4" /></button>
                        <button onClick={resetView} title="Reset view"><Maximize className="w-4 h-4" /></button>
                    </div>

                    <div
                        className="wf-canvas-inner"
                        ref={canvasInnerRef}
                        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}
                    >
                        {/* SVG edges */}
                        <svg className="wf-canvas-edges">
                            {edges.map(edge => {
                                const path = getEdgePath(edge);
                                if (!path) return null;
                                const isSelected = selectedEdgeId === edge.id;
                                const mid = edge.label ? getEdgeMidpoint(edge) : null;
                                return (
                                    <g key={edge.id}>
                                        <path className="edge-hit" d={path} onClick={(e) => { e.stopPropagation(); setSelectedEdgeId(edge.id); }} />
                                        <path className={`edge-line ${isSelected ? 'selected' : ''}`} d={path} />
                                        {/* Edge label (Issue 9) */}
                                        {mid && edge.label && (() => {
                                            const labelWidth = edge.label.length * 6 + 12;
                                            return (
                                                <>
                                                    <rect className="edge-label-bg" x={mid.x - labelWidth / 2} y={mid.y - 9} width={labelWidth} height={18} rx={4} />
                                                    <text className="edge-label-text" x={mid.x} y={mid.y + 3} textAnchor="middle">{edge.label}</text>
                                                </>
                                            );
                                        })()}
                                        {/* Arrow at end */}
                                        {(() => {
                                            const toNode = nodes.find(n => n.id === edge.to);
                                            const fromNode = nodes.find(n => n.id === edge.from);
                                            if (!toNode || !fromNode) return null;
                                            const toSide = edge.manual ? edge.toSide : 'top';
                                            const toPos = getPortPosition(toNode, toSide);
                                            if (!toPos) return null;
                                            const arrowSize = 6;
                                            let points;
                                            if (toSide === 'top') {
                                                points = `${toPos.x - arrowSize},${toPos.y + arrowSize} ${toPos.x},${toPos.y} ${toPos.x + arrowSize},${toPos.y + arrowSize}`;
                                            } else if (toSide === 'bottom') {
                                                points = `${toPos.x - arrowSize},${toPos.y - arrowSize} ${toPos.x},${toPos.y} ${toPos.x + arrowSize},${toPos.y - arrowSize}`;
                                            } else if (toSide === 'left') {
                                                points = `${toPos.x + arrowSize},${toPos.y - arrowSize} ${toPos.x},${toPos.y} ${toPos.x + arrowSize},${toPos.y + arrowSize}`;
                                            } else {
                                                points = `${toPos.x - arrowSize},${toPos.y - arrowSize} ${toPos.x},${toPos.y} ${toPos.x - arrowSize},${toPos.y + arrowSize}`;
                                            }
                                            return <polygon className="edge-arrow" points={points} fill={isSelected ? '#6366F1' : '#A29D96'} />;
                                        })()}
                                        {isSelected && (() => {
                                            const fromNode = nodes.find(n => n.id === edge.from);
                                            const toNode = nodes.find(n => n.id === edge.to);
                                            if (!fromNode || !toNode) return null;
                                            const fromSide = computeFromSide(fromNode, toNode);
                                            const toSide = edge.manual ? edge.toSide : 'top';
                                            const fromPos = getPortPosition(fromNode, fromSide);
                                            const toPos = getPortPosition(toNode, toSide);
                                            const midX = (fromPos.x + toPos.x) / 2;
                                            const midY = (fromPos.y + toPos.y) / 2;
                                            return (
                                                <g className="edge-delete-btn" onClick={(e) => { e.stopPropagation(); deleteEdge(edge.id); }}>
                                                    <circle cx={midX} cy={midY} r={14} fill="#FFFFFF" stroke="#C2363F" strokeWidth={1.5} />
                                                    <path d={`M ${midX - 5} ${midY - 5} L ${midX + 5} ${midY + 5} M ${midX - 5} ${midY + 5} L ${midX + 5} ${midY - 5}`} stroke="#C2363F" strokeWidth={2} strokeLinecap="round" />
                                                </g>
                                            );
                                        })()}
                                    </g>
                                );
                            })}
                            {/* Temp edge while dragging */}
                            {connecting && (() => {
                                const fromNode = nodes.find(n => n.id === connecting.fromNode);
                                if (!fromNode) return null;
                                const fromPos = getPortPosition(fromNode, connecting.fromSide);
                                if (!fromPos) return null;
                                const dx = connecting.mouseX - fromPos.x;
                                const cp1x = fromPos.x + dx * 0.5;
                                return (
                                    <path className="temp-edge" d={`M ${fromPos.x} ${fromPos.y} C ${cp1x} ${fromPos.y}, ${connecting.mouseX} ${connecting.mouseY}, ${connecting.mouseX} ${connecting.mouseY}`} />
                                );
                            })()}
                        </svg>

                        {/* Nodes */}
                        {nodes.map(node => {
                            const cfg = NODE_TYPES[node.type];
                            const isSelected = selectedNodeId === node.id;
                            const isDragging = drag?.kind === 'node' && drag.id === node.id;
                            const isEditable = node.type !== 'start';
                            const outputsLocked = node.type === 'condition';
                            const outputClass = `wf-port wf-port-output${outputsLocked ? ' wf-port-disabled' : ''}`;
                            const outputTitle = outputsLocked ? 'Branches are configured in the Condition editor' : 'Output (drag to connect)';
                            const isUnreachable = node.type !== 'start' && !!node.stepId && !reachableIds.has(node.stepId);
                            return (
                                <div
                                    key={node.id}
                                    className={`wf-node ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
                                    style={{ left: node.x, top: node.y }}
                                    onMouseDown={(e) => onNodeMouseDown(e, node.id)}
                                    onClick={(e) => { e.stopPropagation(); setSelectedNodeId(node.id); setSelectedEdgeId(null); setShowSettings(false); }}
                                >
                                    {/* Ports — top = INPUT, bottom/left/right = OUTPUT */}
                                    {/* All nodes have interactive ports with connector-type enforcement:
                                        OUTPUT ports (bottom/left/right) initiate drags,
                                        INPUT ports (top) receive drops. Condition nodes are included
                                        so users can manually wire edges if needed. */}
                                    <>
                                        <div
                                            className={`wf-port wf-port-top wf-port-input ${connecting ? 'connectable' : ''}`}
                                            data-side="top"
                                            onMouseDown={(e) => onPortMouseDown(e, node.id, 'top')}
                                            onMouseUp={(e) => onPortMouseUp(e, node.id, 'top')}
                                            title="Input (drop target)"
                                        />
                                        <div
                                            className={`${outputClass} wf-port-right`}
                                            data-side="right"
                                            onMouseDown={(e) => onPortMouseDown(e, node.id, 'right')}
                                            onMouseUp={(e) => onPortMouseUp(e, node.id, 'right')}
                                            title={outputTitle}
                                        />
                                        <div
                                            className={`${outputClass} wf-port-bottom`}
                                            data-side="bottom"
                                            onMouseDown={(e) => onPortMouseDown(e, node.id, 'bottom')}
                                            onMouseUp={(e) => onPortMouseUp(e, node.id, 'bottom')}
                                            title={outputTitle}
                                        />
                                        <div
                                            className={`${outputClass} wf-port-left`}
                                            data-side="left"
                                            onMouseDown={(e) => onPortMouseDown(e, node.id, 'left')}
                                            onMouseUp={(e) => onPortMouseUp(e, node.id, 'left')}
                                            title={outputTitle}
                                        />
                                    </>
                                    {/* Delete button — not for start/end */}
                                    {isEditable && (
                                        <div
                                            className="wf-node-delete"
                                            onClick={(e) => { e.stopPropagation(); deleteNode(node.id); }}
                                            title="Delete node"
                                        >
                                            <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M18 6 6 18 M6 6l12 12" />
                                            </svg>
                                        </div>
                                    )}
                                    {/* Content */}
                                    <div className="wf-node-top">
                                        <NodeIcon type={node.type} />
                                        <span className="wf-node-type" style={{ color: cfg?.color }}>{cfg?.label}</span>
                                    </div>
                                    <div className="wf-node-title">{node.title}</div>
                                    <div className="wf-node-meta">{node.meta}</div>
                                    {isUnreachable && (
                                        <div className="wf-node-warn" title="No path from Start reaches this node. It is skipped at runtime.">⚠ Unreachable</div>
                                    )}
                                    {/* Correlation badge — shows source node name */}
                                    {node.type === 'forwarder' && (() => {
                                        const fwd = forwarders.find(f => f.step_id === node.stepId);
                                        if (!fwd?.correlation_source_step_id) return null;
                                        // Look up source forwarder by step_id in the forwarders array,
                                        // not in form.steps (correlation_source_step_id points to a forwarder node)
                                        const sourceFwd = forwarders.find(f => f.step_id === fwd.correlation_source_step_id);
                                        const sourceName = sourceFwd?.name || fwd.correlation_source_step_id;
                                        return (
                                            <div style={{ display: 'inline-block', marginTop: 4, padding: '2px 8px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 600, border: '1px solid #FDE68A' }}>
                                                ↺ Updates: {sourceName}
                                            </div>
                                        );
                                    })()}
                                    {/* Create/Update badge for GHL forwarders */}
                                    {node.type === 'forwarder' && (() => {
                                        const fwd = forwarders.find(f => f.step_id === node.stepId);
                                        if (fwd?.type !== 'gohighlevel') return null;
                                        if (fwd?.correlation_source_step_id) return (
                                            <span style={{ display: 'inline-block', marginTop: 4, marginLeft: 4, padding: '2px 6px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', fontSize: 9, fontWeight: 600 }}>
                                                Update
                                            </span>
                                        );
                                        return (
                                            <span style={{ display: 'inline-block', marginTop: 4, marginLeft: 4, padding: '2px 6px', borderRadius: 4, background: '#D1FAE5', color: '#065F46', fontSize: 9, fontWeight: 600 }}>
                                                Create
                                            </span>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Properties Panel */}
                <div className="wf-props-panel">
                    <div className="wf-props-title">
                        {showSettings ? 'Form Settings' : 'Node Properties'}
                    </div>
                    {showSettings ? (
                        <div>
                            {/* Workflow name */}
                            <div className="wf-props-row"
                                style={{ background: '#F7F6F2', padding: 12, borderRadius: 8, border: '1px solid #E8E5DF' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6E6961', marginBottom: 8,
                                    textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    WORKFLOW NAME
                                </div>
                                <input
                                    type="text"
                                    className="wf-props-input"
                                    value={form?.name || ''}
                                    onChange={(e) => { setForm(prev => ({ ...prev, name: e.target.value })); markDirty(); }}
                                    placeholder="Untitled Workflow"
                                />
                            </div>
                            {/* Event registration (#919): form-wide, so it sits with the other form-wide settings */}
                            <div className="wf-props-row" style={{ marginTop: 8 }}>
                                <EventRegistrationSettings
                                    formId={formId}
                                    isNew={isNew}
                                    value={form?.settings?.event_registration}
                                    hasEmailField={registrationHasEmail}
                                    onChange={updateEventRegistration}
                                    dirty={registrationDirty}
                                />
                            </div>
                            {/* Branding toggle */}
                            <div className="wf-props-row"
                                style={{ background: '#F7F6F2', padding: 12, borderRadius: 8, border: '1px solid #E8E5DF', marginTop: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6E6961', marginBottom: 8,
                                    textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    BRANDING
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={form?.styling?.show_branding !== false}
                                        onChange={(e) => updateStyling('show_branding', e.target.checked)}
                                        style={{ width: 16, height: 16, accentColor: '#1C1917' }}
                                    />
                                    <span style={{ fontSize: 12, fontWeight: 500, color: '#57534E' }}>
                                        Show "Powered by NurtureCRM" branding
                                    </span>
                                </label>
                            </div>
                            {/* Stepper toggle */}
                            <div className="wf-props-row"
                                style={{ background: '#F7F6F2', padding: 12, borderRadius: 8,
                                    border: '1px solid #E8E5DF', marginTop: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6E6961', marginBottom: 8,
                                    textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    STEPPER
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={form?.styling?.show_stepper !== false}
                                        onChange={(e) => updateStyling('show_stepper', e.target.checked)}
                                        style={{ width: 16, height: 16, accentColor: '#1C1917' }}
                                    />
                                    <span style={{ fontSize: 12, fontWeight: 500, color: '#57534E' }}>
                                        Enable stepper
                                    </span>
                                </label>
                                <div style={{ fontSize: 11, color: '#A29D96', marginTop: 4 }}>
                                    Show step progress dots above the form.
                                </div>
                                {/* Stepper state colours (Issue #480) — optional; unset = default look */}
                                {[
                                    { key: 'stepper_completed_color', label: 'Completed colour', fallback: '#10b981' },
                                    { key: 'stepper_active_color', label: 'Active colour', fallback: '#1c1917' },
                                    { key: 'stepper_upcoming_color', label: 'Upcoming colour', fallback: '#e7e5e4' },
                                ].map(({ key, label, fallback }) => {
                                    const hex = form?.styling?.[key] || null;
                                    const fg = hex ? deriveNumeralColor(hex) : null;
                                    const ratio = hex && fg ? contrastRatio(hex, fg) : null;
                                    return (
                                        <div key={key} style={{ marginTop: 10 }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#6E6961', marginBottom: 6 }}>
                                                {label}
                                            </div>
                                            <ColorField
                                                value={hex}
                                                onChange={next => updateStyling(key, next)}
                                                fallback={fallback}
                                                clearLabel="Reset"
                                                placeholder="Default"
                                                swatchLabel={label}
                                                swatchClass="w-8 h-8 rounded border border-stone-200 cursor-pointer"
                                                inputClass="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-md bg-white focus:outline-none"
                                                clearClass="text-xs text-stone-400 hover:text-stone-600 whitespace-nowrap"
                                            />
                                            {hex && ratio != null && ratio < 3 && (
                                                <div style={{ fontSize: 10, color: '#A29D96', marginTop: 3 }}>
                                                    Low contrast — step numbers may be hard to read.
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div style={{ fontSize: 11, color: '#A29D96', marginTop: 6 }}>
                                    Colours for the step dots and connecting lines. Leave unset for the default look.
                                </div>
                                {/* Margin between stepper and form (px) */}
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6E6961', marginBottom: 6 }}>
                                        Margin (px)
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                                        {['top', 'right', 'bottom', 'left'].map(side => (
                                            <div key={side}>
                                                <input
                                                    type="number" min={0} max={200}
                                                    value={form?.styling?.stepper_margin?.[side] ?? ''}
                                                    onChange={e => updateStyling('stepper_margin', {
                                                        ...(form?.styling?.stepper_margin || {}),
                                                        [side]: e.target.value === '' ? null : Math.max(0, Math.min(200, parseInt(e.target.value, 10) || 0)),
                                                    })}
                                                    style={{ width: '100%', padding: '6px 4px', fontSize: 12, textAlign: 'center',
                                                        border: '1px solid #E8E5DF', borderRadius: 6, background: '#fff',
                                                        outline: 'none' }}
                                                    placeholder="0"
                                                />
                                                <p style={{ fontSize: 9, color: '#A29D96', textAlign: 'center', marginTop: 2, textTransform: 'capitalize' }}>
                                                    {side}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#A29D96', marginTop: 4 }}>
                                        Spacing between the stepper and the form. Default is 24px when unset.
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : !selectedNode ? (
                        <div style={{ fontSize: 13, color: '#A29D96', padding: '20px 0', textAlign: 'center' }}>
                            Click a node to view its properties
                        </div>
                    ) : (
                        <div>
                            <div className="wf-props-row">
                                <label className="wf-props-label">Node Type</label>
                                <div style={{ fontSize: 13, fontWeight: 600, color: NODE_TYPES[selectedNode.type]?.color }}>
                                    {NODE_TYPES[selectedNode.type]?.label}
                                </div>
                            </div>
                            <div className="wf-props-row">
                                <label className="wf-props-label">Title</label>
                                {selectedNode.type === 'start' ? (
                                    // Start node is non-editable (always present)
                                    <input className="wf-props-input" value={selectedNode.title} disabled style={{ opacity: 0.6 }} />
                                ) : (
                                    <input
                                        className="wf-props-input"
                                        value={selectedNode.title}
                                        onChange={(e) => { setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, title: e.target.value } : n)); markDirty(); }}
                                    />
                                )}
                            </div>
                            <div className="wf-props-row">
                                <label className="wf-props-label">Description</label>
                                <input
                                    className="wf-props-input"
                                    value={selectedNode.meta}
                                    onChange={(e) => { setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, meta: e.target.value } : n)); markDirty(); }}
                                />
                            </div>
                            <div className="wf-props-row">
                                <label className="wf-props-label">Position</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input className="wf-props-input" type="number" value={Math.round(selectedNode.x)} onChange={(e) => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, x: parseInt(e.target.value) || 0 } : n))} style={{ flex: 1 }} />
                                    <input className="wf-props-input" type="number" value={Math.round(selectedNode.y)} onChange={(e) => setNodes(nodes.map(n => n.id === selectedNode.id ? { ...n, y: parseInt(e.target.value) || 0 } : n))} style={{ flex: 1 }} />
                                </div>
                            </div>
                            {/* Type-specific info */}
                            {selectedNode.type === 'forwarder' && (() => { const fwd = forwarders.find(f => f.step_id === selectedNode.stepId); return fwd ? (
                                <div className="wf-props-row" style={{ background: '#F0FDFA', padding: 10, borderRadius: 8, border: '1px solid #A7F3D0' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1A7F4E', marginBottom: 4 }}>FORWARDER CONFIG</div>
                                        <div style={{ fontSize: 12, color: '#57534E' }}>
                                            <div>Type: {fwd.type === 'gohighlevel' ? 'GoHighLevel' : 'Custom API'}</div>
                                            <div>Status: {fwd.enabled !== false ? 'Active' : 'Inactive'}</div>
                                            {fwd.correlation_source_step_id && (() => { const src = form?.steps?.find(s => s.step_id === fwd.correlation_source_step_id); return <div style={{ marginTop: 4, display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 600, border: '1px solid #FDE68A' }}>↺ Updates: {src?.title || fwd.correlation_source_step_id}</div>; })()}
                                            {fwd.url && <div style={{ fontSize: 11, fontFamily: 'monospace', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>{fwd.url}</div>}
                                        </div>
                                </div>
                            ) : (
                                <div className="wf-props-row" style={{ background: '#F0FDFA', padding: 10, borderRadius: 8, border: '1px solid #A7F3D0' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1A7F4E', marginBottom: 4 }}>FORWARDER CONFIG</div>
                                    <div style={{ fontSize: 12, color: '#A29D96' }}>No forwarder configured yet</div>
                                </div>
                            ); })()}
                            {selectedNode.type === 'condition' && (
                                <div className="wf-props-row" style={{ background: '#FCEFDD', padding: 10, borderRadius: 8, border: '1px solid #FED7AA' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#B45309', marginBottom: 4 }}>CONDITIONAL BRANCH</div>
                                    <div style={{ fontSize: 12, color: '#57534E' }}>
                                        <div>Mode: {selectedNode.meta?.includes('If/Else') ? 'If/Else' : 'Switch Case'}</div>
                                        <div>{selectedNode.meta}</div>
                                    </div>
                                </div>
                            )}
                            {selectedNode.type === 'redirect' && selectedNode.stepId && (() => {
                                const step = (form?.steps || []).find(s => s.step_id === selectedNode.stepId);
                                return (
                                    <div className="wf-props-row" style={{ background: '#EFF6FF', padding: 12, borderRadius: 8, border: '1px solid #BFDBFE' }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6366F1', marginBottom: 8 }}>REDIRECT CONFIGURATION</div>
                                        <label style={{ fontSize: 11, fontWeight: 500, color: '#57534E', display: 'block', marginBottom: 4 }}>Redirect URL</label>
                                        <input
                                            type="text"
                                            value={step?.redirect_url || ''}
                                            onChange={(e) => updateStep(selectedNode.stepId, { redirect_url: e.target.value })}
                                            placeholder="https://example.com/thank-you"
                                            style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid #D6D3D1', borderRadius: 6, fontFamily: 'monospace', boxSizing: 'border-box' }}
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!step?.trigger_submit}
                                                onChange={(e) => updateStep(selectedNode.stepId, { trigger_submit: e.target.checked })}
                                                style={{ width: 16, height: 16, accentColor: '#6366F1' }}
                                            />
                                            <span style={{ fontSize: 12, fontWeight: 500, color: '#57534E' }}>Trigger form submission on reach</span>
                                        </label>
                                        <div style={{ fontSize: 11, color: '#A29D96', marginTop: 4 }}>
                                            When enabled, the form submits in the background before redirecting.
                                        </div>
                                        {/* Query Parameters section */}
                                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #BFDBFE' }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#6366F1', marginBottom: 8 }}>
                                                QUERY PARAMETERS
                                            </div>
                                            <div style={{ fontSize: 11, color: '#A29D96', marginBottom: 8 }}>
                                                Append form field values as URL query parameters on redirect.
                                            </div>
                                            {(step?.query_params || []).map((qp) => (
                                                <div key={qp.id} style={{
                                                    marginBottom: 8, padding: 10, borderRadius: 8,
                                                    background: '#FAFAF9', border: '1px solid #E8E5DF'
                                                }}>
                                                    {/* Line 1: param name + trash */}
                                                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                        <input
                                                            type="text"
                                                            value={qp.param_name || ''}
                                                            onChange={(e) => {
                                                                const sanitized = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                                                                const newQp = [...(step?.query_params || [])];
                                                                const idx = newQp.findIndex(p => p.id === qp.id);
                                                                if (idx !== -1) newQp[idx] = { ...newQp[idx], param_name: sanitized };
                                                                updateStep(selectedNode.stepId, { query_params: newQp });
                                                            }}
                                                            placeholder="param_name"
                                                            title="Only letters, numbers, and underscores allowed"
                                                            style={{
                                                                flex: 1, padding: '6px 8px', fontSize: 12,
                                                                border: '1px solid #D6D3D1', borderRadius: 6,
                                                                fontFamily: 'monospace', boxSizing: 'border-box'
                                                            }}
                                                        />
                                                        <button
                                                            onClick={() => {
                                                                const newQp = (step?.query_params || []).filter(p => p.id !== qp.id);
                                                                updateStep(selectedNode.stepId, { query_params: newQp });
                                                            }}
                                                            title="Remove parameter"
                                                            style={{
                                                                flexShrink: 0, width: 28, height: 28,
                                                                border: '1px solid #E8E5DF', borderRadius: 6,
                                                                background: '#FFFFFF', cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center',
                                                                justifyContent: 'center', color: '#C2363F'
                                                            }}
                                                        >
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                                                stroke="currentColor" strokeWidth="2"
                                                                strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    {/* Line 2: field select */}
                                                    <select
                                                        value={qp.field_id || ''}
                                                        onChange={(e) => {
                                                            const newQp = [...(step?.query_params || [])];
                                                            const idx = newQp.findIndex(p => p.id === qp.id);
                                                            if (idx !== -1) newQp[idx] = { ...newQp[idx], field_id: e.target.value };
                                                            updateStep(selectedNode.stepId, { query_params: newQp });
                                                        }}
                                                        style={{
                                                            width: '100%', marginTop: 6, padding: '6px 8px', fontSize: 12,
                                                            border: '1px solid #D6D3D1', borderRadius: 6,
                                                            background: '#FFFFFF', color: '#1C1917',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        <option value="">— Select field —</option>
                                                        {allFormFields.map(f => (
                                                            <option key={f.field_id} value={f.field_id}>
                                                                {f.label || f.field_id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))}
                                            <button
                                                onClick={() => {
                                                    const newQp = [...(step?.query_params || []), { id: crypto.randomUUID(), param_name: '', field_id: '' }];
                                                    updateStep(selectedNode.stepId, { query_params: newQp });
                                                }}
                                                style={{
                                                    width: '100%', padding: '8px',
                                                    border: '1.5px dashed #D6D3D1', borderRadius: 8,
                                                    background: 'transparent', color: '#78716C',
                                                    fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                                    display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', gap: 6, marginTop: 4
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" strokeWidth="2"
                                                    strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 5v14 M5 12h14" />
                                                </svg>
                                                Add Parameter
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}
                            {/* Connection info */}
                            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #F1F0EB' }}>
                                <div className="wf-props-label" style={{ marginBottom: 8 }}>Connections</div>
                                <div style={{ fontSize: 12, color: '#78716C' }}>
                                    {edges.filter(e => e.from === selectedNode.id).length} outgoing · {edges.filter(e => e.to === selectedNode.id).length} incoming
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Selected edge info */}
                    {selectedEdgeId && (
                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #F1F0EB' }}>
                            <div className="wf-props-label">Selected Connection</div>
                            <div style={{ fontSize: 12, color: '#78716C', marginTop: 4 }}>
                                Click the ✕ on the canvas to delete this connection.
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Navigation Confirm Modal (unsaved canvas changes) */}
            {showNavConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
                        <h3 className="text-lg font-bold text-stone-900 mb-2">Unsaved Changes</h3>
                        <p className="text-sm text-stone-500 mb-4">You have unsaved changes on the canvas. Are you sure you want to leave?</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={cancelLeave} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50">Stay</button>
                            <button onClick={confirmLeave} className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600">Leave</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
}
